import { useEffect, useMemo } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import {
    Card,
    ChangeValue,
    Tooltip,
    TitlePlaceholderLoader,
    ListItemPlaceholderLoader,
} from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { toggleAlternativeSource } from "app/actions/commonActions";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { PulseDot } from "app/components/pulse-dot/PulseDot";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { abbrNumberWithNASupportVisitsFilter, minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import {
    TotalVisitsComponentHistoryHook,
    useTotalVisitsComponentHistoryHook,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TotalVisitsComponentHistoryHook";
import type { IWebsiteAnalysisTrafficRequestParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";

import { useTotalVisitsData } from "./useTotalVisitsData";
import { allTrackers } from "app/services/track/track";

const SlantSeparator = styled.div`
    height: 100%;
    &:before {
        content: "";
        display: block;
        height: 100%;
        width: 1px;
        background-color: ${colorsPalettes.carbon["50"]};
        transform: rotate(12deg);
    }
`;

const StyledTitleLoader = styled(TitlePlaceholderLoader)`
    height: 24px;
`;

const StyledMainTitleLoader = styled(TitlePlaceholderLoader)`
    height: 40px;
`;

const StyledListItemLoader = styled(ListItemPlaceholderLoader)`
    height: 20px;
`;

interface ITotalVisitsWidgetProps {
    params: {
        country: number;
        from: string;
        to: string;
        key: string;
        webSource: string;
        isWindow: boolean;
        isWWW: boolean;
    };
    dateRangeText: string;
    countryText: string;
    countryCode: number;
    showGAApprovedData: boolean;
    isGAPrivate: boolean;
    apiController: string;
    showAddToDashboard?: boolean;
}

export const TotalVisitsWidget = ({
    params,
    dateRangeText,
    countryText,
    countryCode,
    showGAApprovedData,
    isGAPrivate,
    apiController,
    showAddToDashboard = false,
}: ITotalVisitsWidgetProps) => {
    const { translate } = useTranslate();
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger("total-visits-widget");
    const apiParams: IWebsiteAnalysisTrafficRequestParams = useMemo(
        () => ({
            country: params.country.toString(),
            from: params.from,
            to: params.to,
            keys: params.key,
            webSource: params.webSource,
            isWindow: params.isWindow,
            includeSubDomains: !params.isWWW,
            timeGranularity: "Monthly",
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ShouldGetVerifiedData: showGAApprovedData,
        }),
        [
            params.country,
            params.from,
            params.to,
            params.key,
            params.webSource,
            params.isWindow,
            params.isWWW,
            showGAApprovedData,
        ],
    );

    const { data, isLoading, isError } = useTotalVisitsData(apiParams, apiController);

    const dataObj = useMemo(() => data?.Data?.[params.key], [data, params.key]);

    const {
        ctaRef,
        isHistoryHookDisplayed,
        isPulsarShown,
        majorPeakType,
        onMouseEnter,
        onHookClick,
        onCloseCta,
        onOpenCta,
        onMouseLeave,
    } = useTotalVisitsComponentHistoryHook();

    const hasChangeValue = !!dataObj?.Change;

    const dispatch = useDispatch();

    useEffect(() => {
        if (!data) {
            return;
        }

        const fromAlternativeSources = !!data?.FromAlternativeSources;
        dispatch(toggleAlternativeSource(fromAlternativeSources));
        if (data.FromAlternativeSources === true) {
            allTrackers.dispatchTrackEvent(
                "Internal Link",
                "show",
                `WebAnalysis/TrafficAndEngagement/AlternativeSource/${params.key || ""}`,
            );
        }

        return () => {
            dispatch(toggleAlternativeSource(false));
        };
    }, [data?.FromAlternativeSources, dispatch, params.key]);

    const isEmpty = dataObj?.TotalVisits === 0;

    const titleIcons = (
        <Flex alignItems="center">
            {showGAApprovedData ? (
                <span data-automation="total-visits-widget-title-ga-verified">
                    <GAVerifiedContainer
                        size="SMALL"
                        isActive={true}
                        isPrivate={isGAPrivate}
                        tooltipAvailable={true}
                        metric="EngagementVisits"
                        tooltipIsActive={false}
                        top={4}
                    />
                </span>
            ) : null}
            <span data-automation="total-visits-widget-title-tooltip">
                <Tooltip content={translate("wa.ao.totalvisits.tooltip")}>
                    <Info24px
                        size={24}
                        color={colorsPalettes.carbon[200]}
                        data-automation="total-visits-widget-title-info-icon"
                    />
                </Tooltip>
            </span>
        </Flex>
    );

    const cardHeader = (
        <Card.Header withDivider>
            <Card.Title>
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        data-automation="total-visits-widget-title"
                    >
                        {translate("wa.ao.totalvisits")}
                    </Text>
                    {titleIcons}
                </Flex>
                <Card.Query>
                    <Card.QueryItem
                        iconName="daily-ranking"
                        data-automation="total-visits-widget-date-range"
                        {...durationTriggerProps}
                    >
                        {dateRangeText}
                    </Card.QueryItem>
                    <Flex
                        gap="8px"
                        alignItems="center"
                        data-automation="total-visits-widget-country"
                        {...countryTriggerProps}
                    >
                        <SWReactCountryIcons
                            countryCode={countryCode}
                            size="xs"
                        />
                        <Text
                            fontVariant="chip1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {countryText}
                        </Text>
                    </Flex>
                </Card.Query>
            </Card.Title>
            {showAddToDashboard ? (
                <AddToDashboard
                    modelType="fromWebsite"
                    metric="EngagementVisits"
                    type="SingleMetric"
                    webSource={params.webSource as IWidgetModelTypesWebSource}
                    filters={{
                        ShouldGetVerifiedData: showGAApprovedData,
                    }}
                />
            ) : null}
        </Card.Header>
    );

    if (isHistoryHookDisplayed) {
        return (
            <TotalVisitsComponentHistoryHook
                ctaRef={ctaRef}
                majorPeakType={majorPeakType}
                onHookClick={onHookClick}
                onCloseClicked={onCloseCta}
                onMouseLeave={onMouseLeave}
            />
        );
    }

    if (isLoading) {
        return (
            <Card data-automation="total-visits-widget">
                <Card.Header>
                    <Card.Title>
                        <StyledTitleLoader />
                    </Card.Title>
                </Card.Header>
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    padding="16px 24px"
                    gap="8px"
                    flex="1"
                >
                    <StyledMainTitleLoader />
                    <StyledListItemLoader />
                </Flex>
            </Card>
        );
    }

    return (
        <Card
            data-automation="total-visits-widget"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {isError || isEmpty ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    flex="1"
                    height="100%"
                >
                    <NoDataLandscape
                        title="global.nodata.notavilable"
                        subtitle={null}
                    />
                </Flex>
            ) : (
                <>
                    {cardHeader}
                    <Flex
                        flexDirection="column"
                        justifyContent="center"
                        padding="16px 24px"
                        gap="8px"
                        flex="1"
                    >
                        <Flex
                            alignItems="center"
                            gap="16px"
                        >
                            <Text
                                fontVariant="h2"
                                color={colorsPalettes.carbon[500]}
                            >
                                {data?.FromAlternativeSources
                                    ? abbrNumberWithNASupportVisitsFilter()(dataObj?.TotalVisits)
                                    : minVisitsAbbrFilter()(dataObj?.TotalVisits)}
                            </Text>
                            {dataObj?.ShareOfVisits < 1 ? <SlantSeparator /> : null}
                            {dataObj?.ShareOfVisits < 1 ? (
                                <Text
                                    fontVariant="h4"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {percentageSignFilter()(dataObj?.ShareOfVisits, 2)}
                                </Text>
                            ) : null}
                        </Flex>
                        <Flex
                            gap="8px"
                            alignItems="center"
                        >
                            {dataObj?.Change ? (
                                <ChangeValue
                                    value={percentageSignFilter()(dataObj?.Change, 2)}
                                    isDecrease={dataObj?.Change < 0}
                                    unsigned={dataObj?.Change === 0}
                                />
                            ) : null}
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate(
                                    hasChangeValue
                                        ? "wa.ao.totalvisits.change.lastmonth"
                                        : "wa.ao.totalvisits.change.na",
                                )}
                            </Text>
                            {isPulsarShown ? (
                                <button
                                    style={{ display: "contents" }}
                                    onClick={onOpenCta}
                                >
                                    <PulseDot
                                        pulseDiameter={12}
                                        centerDiameter={12}
                                        padding={0}
                                        color={colorsPalettes.blue[400]}
                                    />
                                </button>
                            ) : null}
                        </Flex>
                    </Flex>
                </>
            )}
        </Card>
    );
};
